import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
    Autocomplete,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Typography,
    Stack,
    CircularProgress,
    Tooltip
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

const MIN_QUERY_LENGTH = 3;
const SUGGESTIONS_DEBOUNCE_MS = 350;

const CarrierPickupSearch = ({ carrier, initialQuery = '', onSearch, onClear, loading }) => {
    const [query, setQuery] = useState(initialQuery);
    const [addressOptions, setAddressOptions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const debounceTimerRef = useRef(null);
    const suggestionsRequestRef = useRef(0);
    const placesServiceRef = useRef(null);

    const clearPendingSearch = () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    };

    const getPlacesService = () => {
        if (placesServiceRef.current) return placesServiceRef.current;
        const ctor = window?.google?.maps?.places?.AutocompleteService;
        if (!ctor) return null;
        placesServiceRef.current = new ctor();
        return placesServiceRef.current;
    };

    const fetchAddressSuggestions = (rawValue) => {
        const normalizedQuery = rawValue.trim();
        if (normalizedQuery.length < MIN_QUERY_LENGTH) {
            setAddressOptions([]);
            setLoadingSuggestions(false);
            return;
        }

        const service = getPlacesService();
        if (!service) {
            setAddressOptions([]);
            setLoadingSuggestions(false);
            return;
        }

        const requestId = suggestionsRequestRef.current + 1;
        suggestionsRequestRef.current = requestId;
        setLoadingSuggestions(true);

        service.getPlacePredictions(
            {
                input: normalizedQuery,
                componentRestrictions: { country: 'cl' },
                types: ['geocode'],
            },
            (predictions, status) => {
                if (requestId !== suggestionsRequestRef.current) return;

                const expectedStatus = window?.google?.maps?.places?.PlacesServiceStatus?.OK;
                if (!predictions || status !== expectedStatus) {
                    setAddressOptions([]);
                    setLoadingSuggestions(false);
                    return;
                }

                setAddressOptions(predictions.map((prediction) => prediction.description));
                setLoadingSuggestions(false);
            }
        );
    };

    const triggerCarrierPointsSearch = (rawAddress = query, options = {}) => {
        const normalizedQuery = String(rawAddress || '').trim();
        if (!normalizedQuery) return;
        onSearch(normalizedQuery, { notify: true, ...options });
    };

    useEffect(() => {
        return () => {
            clearPendingSearch();
        };
    }, []);

    useEffect(() => {
        setQuery(initialQuery || '');
        setAddressOptions([]);
    }, [carrier, initialQuery]);

    const handleQueryChange = (_event, nextQuery, reason) => {
        const normalizedQuery = (nextQuery || '').trim();
        setQuery(nextQuery || '');

        clearPendingSearch();

        if (reason === 'reset') {
            return;
        }

        if (reason === 'clear' || normalizedQuery.length === 0) {
            setAddressOptions([]);
            onClear?.();
            return;
        }

        if (normalizedQuery.length < MIN_QUERY_LENGTH) {
            setAddressOptions([]);
            return;
        }

        debounceTimerRef.current = setTimeout(() => {
            fetchAddressSuggestions(nextQuery || '');
        }, SUGGESTIONS_DEBOUNCE_MS);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && query.trim().length > 0) {
            e.preventDefault();
            triggerCarrierPointsSearch();
        }
    };

    return (
        <Paper sx={{ mb: 1, p: 2 }}>
            <Stack spacing={1}>
                <Typography variant="body2">
                    Buscar punto de retiro {carrier}
                </Typography>

                <Autocomplete
                    freeSolo
                    options={addressOptions}
                    inputValue={query}
                    onInputChange={handleQueryChange}
                    onChange={(_event, selectedValue, reason) => {
                        if (reason !== 'selectOption' || typeof selectedValue !== 'string') return;

                        clearPendingSearch();
                        setAddressOptions([]);
                        setQuery(selectedValue);
                        triggerCarrierPointsSearch(selectedValue);
                    }}
                    loading={loadingSuggestions}
                    filterOptions={(options) => options}
                    noOptionsText={
                        query.trim().length >= MIN_QUERY_LENGTH
                            ? 'Sin sugerencias'
                            : 'Escribe al menos 3 caracteres'
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            placeholder="Escribe una dirección"
                            fullWidth
                            onKeyDown={handleKeyDown}
                            helperText={
                                query.trim().length >= MIN_QUERY_LENGTH
                                    ? 'Selecciona una sugerencia, presiona Enter o usa Buscar para consultar puntos del carrier.'
                                    : 'Escribe al menos 3 caracteres para sugerencias.'
                            }
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {(loading || loadingSuggestions) ? (
                                            <CircularProgress size={16} sx={{ mr: 0.5 }} />
                                        ) : null}
                                        <InputAdornment position="end" sx={{ ml: 0.5 }}>
                                            <Tooltip title="Buscar puntos de retiro">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={triggerCarrierPointsSearch}
                                                        disabled={loading || query.trim().length === 0}
                                                    >
                                                        <SearchOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </InputAdornment>
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />

                {query.trim().length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                        Dirección ingresada: {query.trim()}
                    </Typography>
                )}
            </Stack>
        </Paper>
    );
};

export default CarrierPickupSearch;
